import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import type { AiAttachableDocument } from '@accounting/shared';
import { ChatComposer } from '../../components/ai/ChatComposer';
import { ChatTranscript } from '../../components/ai/ChatTranscript';
import { ConversationList } from '../../components/ai/ConversationList';
import { useAiChat } from '../../hooks/useAiChat';

export const Route = createFileRoute('/_auth/ask-ai')({
  component: AskAiPage,
});

const EXAMPLES = [
  'Which companies do I have, and which are active?',
  "Summarize this year's profit & loss for each company.",
  'What bank transactions are still uncategorized?',
  'Are my books healthy right now? What needs attention?',
];

function AskAiPage() {
  const chat = useAiChat();
  const [showThreads, setShowThreads] = useState(true);

  // File names for the 📎 line above a sent message. The composer owns which
  // documents are staged; this only remembers what each id was called, so a
  // message that has already been sent still shows a name rather than a UUID.
  const [documentNames, setDocumentNames] = useState<Map<string, string>>(new Map());

  const onAttachmentsChange = useCallback((docs: AiAttachableDocument[]) => {
    if (docs.length === 0) return;
    setDocumentNames((prev) => {
      const next = new Map(prev);
      for (const d of docs) next.set(d.id, d.fileName);
      return next;
    });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      <div
        style={{
          marginBottom: 8,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, margin: '0 0 2px' }}>
            {chat.activeConversation?.title ?? 'Ask AI'}
          </h1>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Ask anything about your books. The assistant reads your live data to answer — it can
            look things up but never changes anything.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowThreads((v) => !v)}
          title={showThreads ? 'Hide saved chats' : 'Show saved chats'}
        >
          {showThreads ? '⟨ Chats' : 'Chats ⟩'}
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
        {showThreads && (
          <ConversationList
            conversations={chat.conversations}
            loading={chat.conversationsLoading}
            activeId={chat.activeId}
            archived={chat.archived}
            onSetArchived={chat.setArchived}
            onSelect={chat.selectConversation}
            onNew={() => void chat.startConversation()}
            onRename={(id, title) => chat.rename.mutate({ id, title })}
            onArchive={(id, value) => chat.setArchivedFlag.mutate({ id, value })}
            onDelete={(id) => chat.remove.mutate(id)}
          />
        )}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <ChatTranscript
            messages={chat.messages}
            streaming={chat.streaming}
            activeTool={chat.activeTool}
            loading={chat.loadingThread}
            examples={EXAMPLES}
            onExample={(text) => void chat.send(text)}
            documentNames={documentNames}
          />

          {chat.error && (
            <p className="field-error" style={{ marginTop: 8 }}>
              {chat.error}
            </p>
          )}

          <ChatComposer
            streaming={chat.streaming}
            canRegenerate={chat.canRegenerate}
            onSend={(text, ids) => void chat.send(text, ids)}
            onStop={chat.stop}
            onRegenerate={() => void chat.regenerate()}
            onAttachmentsChange={onAttachmentsChange}
          />
        </div>
      </div>
    </div>
  );
}
